import { ReportChapterObject } from '../../base';
import * as CaseStudies from '../../content/case-study';
import { createScores } from '../../base/scoreHelper';

export const technicalImplementationsRefactoring: Omit<ReportChapterObject, 'number'> = {
    id: 'technical-implemetations-refactoring',
    caption: `Playbook: Refactoring Legacy Code`,
    title: `Hybrid AI Approaches Accelerate Accurate Large-Scale Code Modernization`,
    description: `Combining AST transformations with LLMs in structured pipelines, as demonstrated by Slack and Airbnb, enables accurate and scalable legacy code modernization. This hybrid approach, enhanced by iterative feedback loops and context-rich prompts, significantly accelerates large-scale migrations, reduces conversion errors, improves code quality through automated refactoring and testing, and ensures security via private infrastructure.`,
    editedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // 1 day ago
    sections: [
        {
            id: 'overview-approaches',
            title: `Hybrid Models Balance Accuracy and Flexibility in Code Transformation`,
            caption: `AST vs. LLM`,
            content: [
                {
                    kind: 'text',
                    text: `Multiple organizations have successfully integrated generative AI (GenAI) into their software development workflows to optimize legacy code and do large scale code migrations. Two broad paradigms have emerged:`,
                },
                {
                    kind: 'heading',
                    text: 'Traditional, rule-based transformations (AST Codemods)',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `Relies on abstract syntax tree (AST) parsing to systematically refactor or migrate code. This approach offers deterministic conversions, beneficial for highly structured or repetitive changes. However, it requires extensive, carefully maintained rule sets, which can become cumbersome for large or highly variable codebases.`,
                },
                {
                    kind: 'heading',
                    text: 'Large Language Model (LLM)–driven conversions',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `Uses AI models (e.g., ChatGPT, Claude, etc.) to generate or refactor code, often with remarkable speed. These models provide contextual or “human-like” understanding, addressing limitations of pure AST transformations. They are, however, prone to “hallucinations” (nonsensical outputs) or inconsistent performance if prompts are not carefully designed.`,
                },
                {
                    kind: 'text',
                    text: `Increasingly, a hybrid model that combines AST-driven tools with LLMs has proven most robust, giving organizations more control over how each file or function is processed and validated.`,
                },
                {
                    kind: 'text',
                    text: `“Our attempts to create a universal prompt for all requests were met with challenges, and the standalone application of AI failed to provide the consistent results we sought.” (Slack)`,
                },
            ],
            keywords: [],
            sources: [CaseStudies.slackCaseStudy],
        },
        {
            id: 'ast-transformations',
            title: `ASTs Provide a Reliable but Limited Foundation for Code Migration`,
            caption: `AST Details`,
            content: [
                {
                    kind: 'text',
                    text: 'Organizations like Slack initially invested heavily in AST-based codemods to tackle large-scale migration tasks, such as converting from Enzyme to React Testing Library (RTL). By traversing a codebase as a tree of nodes:',
                },
                {
                    kind: 'heading',
                    text: 'Rule-Based Refactoring',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'ASTs excel at discrete, well-defined changes—e.g., renaming methods or changing import statements. Slack’s early efforts targeted the top 10 most-used Enzyme methods for migration before expanding to all 65.',
                },
                {
                    kind: 'heading',
                    text: 'Challenges in Cumulative Growth of Rules',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: 'Each new Enzyme method or variation required specialized logic, making the codebase of rules “cumbersome and harder to maintain.”',
                },
                {
                    kind: 'text',
                    text: 'Missing context on the rendered DOM or component internals limited AST’s ability to accurately capture the test’s intent.',
                },
                {
                    kind: 'text',
                    text: '“One important requirement for our conversion was achieving 100%-correct transformations... AST lacks the capability to incorporate contextual information such as the rendered component DOM.” (Slack)',
                },
                {
                    kind: 'divider',
                },
                {
                    kind: 'text',
                    text: 'Despite these drawbacks, ASTs remain a valuable foundation: they provide a structured, deterministic layer of transformations and can help segment “simple” tasks before more advanced or context-sensitive tasks are handed off to an LLM.',
                },
            ],
            keywords: [],
            sources: [CaseStudies.slackCaseStudy],
        },
        {
            id: 'llm-code-conversion',
            title: `Structured Inputs and Feedback Loops Improve LLM Code Conversion`,
            caption: `LLM Techniques`,
            content: [
                {
                    kind: 'text',
                    text: `Slack, Airbnb, and other teams have reported using modern LLMs (e.g., OpenAI, Claude) as a complementary strategy.`,
                },
                {
                    kind: 'heading',
                    text: 'Prompt Engineering & Structured Inputs',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `Minimal or poorly structured prompts can lead to variable, occasionally incorrect refactoring suggestions. Adding code snippets, relevant context from AST outputs, or error messages (compilation or test failures) significantly boosts accuracy. Airbnb found success by systematically including “the source code of the component under test, the test file… validation failures… and common solutions.”`,
                },
                {
                    kind: 'heading',
                    text: 'Iterative Feedback Loop',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `When the LLM-generated code fails a compilation step or a test assertion, the error is fed back into the model. This trial-and-error approach continually refines outputs until the code meets acceptance criteria. Airbnb utilized multiple retries and dynamic prompts to brute force the path to correctness, improving outcomes at scale. “We found the most effective route to improve outcomes was simply brute force: retry steps multiple times until they passed or we reached a limit.” (Airbnb)`,
                },
                {
                    kind: 'heading',
                    text: 'Contextual Awareness & DOM Tree Collection',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `Slack’s DOM tree collection captures rendered component trees before conversion, giving the LLM crucial insight into the final structure that React Testing Library (RTL) emphasizes. This additional context drastically reduces guesswork, mitigating problems where LLM output might be hallucinated or misaligned. “By automating the conversion of simpler cases and providing annotations for all other instances through comments, we successfully minimized hallucinations and nonsensical conversions from the LLM.” (Slack)`,
                },
            ],
            keywords: [],
            sources: [CaseStudies.slackCaseStudy, CaseStudies.airbnbCaseStudy],
        },
        {
            id: 'hybrid-model-integration',
            title: `Hybrid Models Combine Structure and Context for Accurate Conversion`,
            caption: `Hybrid Integration`,
            content: [
                {
                    kind: 'text',
                    text: `A standout strategy is to combine deterministic AST transformations with the flexible reasoning of LLMs.`,
                },
                {
                    kind: 'heading',
                    text: 'Pre-Processing with AST',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `The AST engine handles the easier or more mechanical modifications—renaming methods, adjusting imports, adding stubs for new test patterns. Code is annotated or partially updated with comments that highlight sections needing deeper, context-based changes.`,
                },
                {
                    kind: 'heading',
                    text: 'Prompt Enhancement',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `The partially migrated code, with in-code comments, placeholders, or specific error messages, is then inserted into structured prompts for the LLM. This ensures the LLM sees both the newly formed skeleton from the AST and the legacy logic that still needs transformation.`,
                },
                {
                    kind: 'heading',
                    text: 'Post-Processing & Validation',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `Automated validation steps follow to ensure the final code meets style guides, compiles successfully, and passes test suites. If errors arise, a feedback loop is triggered, and the LLM is given the new error details to refine its output.`,
                },
                {
                    kind: 'text',
                    text: `“By embedding the partially converted AST outputs and detailed in-code comments into the LLM prompt, Slack successfully minimized hallucinations and improved conversion accuracy.” (Slack)`,
                },
            ],
            keywords: [],
            sources: [CaseStudies.slackCaseStudy],
        },
        {
            id: 'pipelines-state-machines',
            title: `Scalable Pipelines Improve Speed and Reliability of Code Transformation`,
            caption: `Operationalization`,
            content: [
                {
                    kind: 'text',
                    text: `Organizations like Airbnb illustrate how to operationalize these hybrid methods at scale.`,
                },
                {
                    kind: 'heading',
                    text: 'State-Machine Processing',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `Each file moves through multiple “states” (e.g., parse, transform, validate) in a linear or branching flow. The pipeline only transitions to the next stage after success in the current step, preventing partially broken code from advancing.`,
                },
                {
                    kind: 'heading',
                    text: 'Systematic Feedback & Re-Runs',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `Failed validation triggers an automatic re-run or “retry loop.” Prompts are updated with recent errors and partial results, enabling continuous improvement.`,
                },
                {
                    kind: 'heading',
                    text: 'Nightly Batch Runs & On-Demand Conversion',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `Slack’s pipeline processes hundreds of files overnight, while developers can also request “2–5 minute conversions on-demand.” This combination of scheduled mass updates and targeted immediate refactoring streamlines the entire migration and maintenance process.`,
                },
                {
                    kind: 'text',
                    text: `“We modeled this flow like a state machine, moving the file to the next state only after validation on the previous state passed.” (Airbnb)`,
                },
            ],
            keywords: [],
            sources: [CaseStudies.airbnbCaseStudy, CaseStudies.slackCaseStudy],
        },
        {
            id: 'incremental-testing-refactoring',
            title: `LLMs Enable Incremental Testing, Refactoring, and Documentation`,
            caption: `Testing & Docs`,
            content: [
                {
                    kind: 'text',
                    text: `In a separate but related workflow, another popular use case for LLMs involves automatically generating and refining unit tests.`,
                },
                {
                    kind: 'heading',
                    text: 'Class-Level Breakdown',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `Codebases are naturally divided by classes (in Java) or modules (in other languages). Each class is processed independently, avoiding token-limit issues in the LLM and keeping the generation process granular.`,
                },
                {
                    kind: 'heading',
                    text: 'Iterative Correction',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `If generated tests fail to compile or produce runtime errors, the LLM is supplied with the error messages to produce revised tests. This loop continues until a set threshold is met, such as compilation success or an acceptable mutation score.`,
                },
                {
                    kind: 'heading',
                    text: 'Automated Code Refactoring',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `LLM-based suggestions also target high-complexity areas, splitting long methods or reducing cyclomatic complexity. Post-processing integrates static code analysis (e.g., checkstyle, lint) to confirm that complexity improvements were successful. “For example, we run our checkstyle… if a method is too long, it gives us the same method with the same parameters just split into smaller methods.” (Incremental Refactoring Approach)`,
                },
                {
                    kind: 'heading',
                    text: 'Documentation Generation',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `A separate “document AI agent” can produce clear, domain-specific explanations for newly refactored methods and classes. This reduces onboarding overhead by providing a business-friendly interpretation of how certain components work. “This agent… creates functional specification from sources… it tells you what it does as an output.” (Incremental Refactoring Approach)`,
                },
            ],
            keywords: [],
            sources: [CaseStudies.ciscoCaseStudy],
        },
        {
            id: 'privacy-infrastructure',
            title: `Privacy-First Deployments Safeguard AI Code Workflows`,
            caption: `Security`,
            content: [
                {
                    kind: 'text',
                    text: `While AI-based transformations are powerful, organizations must weigh the security implications.`,
                },
                {
                    kind: 'heading',
                    text: 'Private Infrastructure',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `Some solutions run on private servers or local machines to ensure code never leaves the company’s network perimeter. Slack and Airbnb both use carefully scoped internal endpoints or have partnerships with vendors that guarantee compliance with security requirements.`,
                },
                {
                    kind: 'heading',
                    text: 'Lightweight Deployment',
                    level: 4,
                },
                {
                    kind: 'text',
                    text: `When running entirely on local hardware with GPU acceleration, solutions remain cost-effective and eliminate the risk of leaking proprietary code. For large enterprises, containerized deployments with resource isolation are common.`,
                },
                {
                    kind: 'text',
                    text: `“It runs on my laptop… there is completely zero problems with data privacy because we don’t send it [offsite].” (Incremental Refactoring Approach)`,
                },
            ],
            keywords: [],
            sources: [CaseStudies.slackCaseStudy, CaseStudies.airbnbCaseStudy],
        },
    ],
};
